import React from "react";
import { FaArrowRight, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { cmsApi } from "../../services/cms";
import { useCMS } from "../../hooks/useCMS";

const normalizeCountry = (value?: string | null) =>
  (value || "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim();

const canonicalCountry = (value?: string | null) => {
  const normalized = normalizeCountry(value);
  if (!normalized) return "";
  if (normalized === "uae" || normalized === "united arab emirates") {
    return "united arab emirates";
  }
  if (normalized === "in" || normalized === "india") return "india";
  return normalized;
};

const siteRegion = (import.meta.env.VITE_SITE_REGION || "IN").toUpperCase();
const siteCountry = siteRegion === "AE" ? "united arab emirates" : "india";
const isMatchingCountry = (country?: string | null) =>
  canonicalCountry(country) === siteCountry;

const getDestinationCountry = (destination: any) =>
  destination?.country ||
  destination?.leadCountry ||
  destination?.siteCountry ||
  destination?.sourceCountry ||
  destination?.market ||
  null;

const getMediaUrl = (value: any): string => {
  if (typeof value === "string") return value.trim();
  if (!value) return "";

  return (
    value.title_image ||
    value.titleImage ||
    value.mediaUrl ||
    value.imageUrl ||
    value.fileUrl ||
    value.assetUrl ||
    value.url ||
    value.image ||
    value.src ||
    value.thumbnailUrl ||
    getMediaUrl(value.media) ||
    ""
  ).trim();
};

const getDestinationImage = (destination: any) => {
  const media = destination?.media || {};
  const gallery = Array.isArray(media.gallery) ? media.gallery : [];
  const images = Array.isArray(media.images) ? media.images : [];

  return (
    getMediaUrl(media.title_image) ||
    getMediaUrl(media.titleImage) ||
    getMediaUrl(destination?.thumbnailUrl) ||
    getMediaUrl(destination?.heroImageUrl) ||
    getMediaUrl(gallery[0]) ||
    getMediaUrl(images[0]) ||
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80"
  );
};

const PopularSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: destinations, loading } = useCMS<any[]>(() => cmsApi.getDestinations('isActive=true&isPopular=true'));
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const itemsPerPage = 3;

  const allDestinations = destinations || [];
  const taggedDestinations = allDestinations.filter((d: any) =>
    Boolean(canonicalCountry(getDestinationCountry(d)))
  );
  const matchingCountryDestinations = allDestinations.filter((d: any) =>
    isMatchingCountry(getDestinationCountry(d))
  );
  const untaggedDestinations = allDestinations.filter(
    (d: any) => !canonicalCountry(getDestinationCountry(d))
  );

  const filteredDestinations =
    matchingCountryDestinations.length > 0
      ? [...matchingCountryDestinations, ...untaggedDestinations]
      : taggedDestinations.length > 0
        ? untaggedDestinations
        : allDestinations;
  const totalItems = filteredDestinations.length;

  React.useEffect(() => {
    setCurrentIndex(0);
  }, [totalItems]);

  const handleNext = () => {
    if (currentIndex + itemsPerPage < totalItems) {
      setCurrentIndex(currentIndex + itemsPerPage);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - itemsPerPage);
    }
  };

  const visibleItems =
    filteredDestinations.slice(currentIndex, currentIndex + itemsPerPage) || [];

  if (loading) return <div className="py-20 text-center">Loading...</div>;

  return (
    <section id="destinations" className="py-20 bg-brand-gray relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-16">
          <span className="text-brand-pink text-sm font-semibold tracking-widest uppercase">
            {t("Curated Collection")}
          </span>

          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-brand-dark mt-2 mb-4">
            {t("Popular Destinations")}
          </h2>

          <p className="text-brand-muted mx-auto max-w-2xl">
            {t("Popular outbound picks for Indian travellers, from Europe favorites to easy short-haul escapes.")}
          </p>
        </div>

        <div className="relative">

          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="absolute left-2 sm:left-0 top-1/2 -translate-y-1/2 sm:-translate-x-4 z-10 bg-white shadow-md rounded-full w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center hover:bg-brand-pink hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaChevronLeft />
          </button>

          <div className="flex gap-4 sm:gap-8 overflow-hidden pb-2">
            {visibleItems.map((destination: any) => (
              <div
                key={destination.id}
                onClick={() => navigate(`/destination-detail/${destination.slug}`)}
                className="group relative rounded-3xl overflow-hidden hover-lift card-shadow bg-white h-[320px] sm:h-[400px] cursor-pointer flex-shrink-0 w-full sm:w-[calc((100%-64px)/3)] snap-start"
              >
                <img
                  src={getDestinationImage(destination)}
                  alt={destination.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>

                <div className="absolute bottom-0 left-0 w-full p-5 sm:p-8 translate-y-0 sm:translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="font-heading text-xl sm:text-2xl font-bold text-white mb-2">
                    {destination.name}
                  </h3>

                  <p className="text-gray-300 text-xs sm:text-sm sm:max-h-0 overflow-hidden sm:group-hover:max-h-20 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 delay-100 line-clamp-3">
                    {destination.shortDescription || destination.description}
                  </p>

                  <span className="inline-flex items-center text-brand-pink text-xs sm:text-sm font-medium uppercase tracking-wider mt-3 sm:mt-4">
                    {t("Explore")}
                    <FaArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" />
                  </span>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={currentIndex + itemsPerPage >= totalItems}
            className="absolute right-2 sm:right-0 top-1/2 -translate-y-1/2 sm:translate-x-4 z-10 bg-white shadow-md rounded-full w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center hover:bg-brand-pink hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaChevronRight />
          </button>
        </div>

        <div className="mt-12 text-center">
          <button onClick={() => navigate("/destination")} className="px-8 py-3 rounded-full border border-brand-dark text-brand-dark font-medium hover:bg-brand-dark hover:text-white transition-all duration-300">
            {t("View All Destinations")}
          </button>
        </div>
      </div>
    </section>
  );
};

export default PopularSection;
